import { useState } from "react";
import "./NemaAssuranceSection.css";

const GUARANTEES = [
  {
    id: "verified",
    icon: "verified",
    title: "NCID Identity Verification",
    body: "NCID provides identity verification for care professionals, shown directly on their profile on NEMA CareHub.",
  },
  {
    id: "control",
    icon: "control",
    title: "Direct Hiring Control",
    body: "You interview, discuss the rate, set the schedule and choose the professional yourself. No agency assigns someone to your family.",
  },
  {
    id: "pricing",
    icon: "pricing",
    title: "Transparent Pricing",
    body: "Review listed rates and applicable platform fees before you hire, then pay the professional directly.",
  },
  {
    id: "replacement",
    icon: "background",
    title: "Replacement Assistance",
    body: "If your professional becomes unavailable, replacement assistance is subject to the applicable replacement policy.",
  },
];

const COMPARE_ROWS = [
  {
    feature: "Choosing a professional",
    nema: "Review available profiles and choose whom to contact.",
    agency: "Selection and assignment processes vary by agency.",
  },
  {
    feature: "Pricing",
    nema: "Review listed rates and applicable platform fees.",
    agency: "Fees and pricing depend on agency terms.",
  },
  {
    feature: "Payment",
    nema: "Pay the care professional directly.",
    agency: "Payment arrangements depend on the agency model.",
  },
  {
    feature: "Identity verification",
    nema: "NCID provides identity verification.",
    agency: "Verification processes vary by agency.",
  },
  {
    feature: "Scheduling",
    nema: "Discuss schedules directly with your chosen professional.",
    agency: "Scheduling arrangements depend on the agency service.",
  },
  {
    feature: "Care coordination",
    nema: "Optional Care Manager support for staffing and scheduling.",
    agency: "Coordination services depend on the agency offering.",
  },
];

function AssuranceIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.75",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "shield":
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "verified":
      return (
        <svg {...common}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <polyline points="16 11 18 13 22 9" />
        </svg>
      );
    case "background":
      return (
        <svg {...common}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "control":
      return (
        <svg {...common}>
          <path d="M12 3v18" />
          <path d="M5 7h14" />
          <path d="M5 7 2.5 13a3.5 3.5 0 1 0 7 0L7 7" />
          <path d="m19 7-2.5 6a3.5 3.5 0 1 0 7 0L19 7" />
          <path d="M8 21h8" />
        </svg>
      );
    case "pricing":
      return (
        <svg {...common}>
          <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <circle cx="7" cy="7" r="1.35" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return null;
  }
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3.5 8h9M9 4.5 12.5 8 9 11.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function NemaAssuranceSection({ onExplore }) {
  const [tab, setTab] = useState("guarantees");

  return (
    <section className="nema-assurance" aria-labelledby="nema-assurance-heading">
      <div className="nema-assurance__intro">
        <span className="nema-assurance__eyebrow">
          <AssuranceIcon name="shield" />
          THE NEMA ASSURANCE
        </span>
        <h2 id="nema-assurance-heading">
          Why Indian families choose <span>direct-hire care</span>
        </h2>
        <p className="nema-assurance__sub">
          We&apos;re redefining home healthcare by putting transparency, verification and direct
          control back in your hands.
        </p>
      </div>

      <div className="nema-assurance__toggle-wrap">
        <div className="nema-assurance__toggle" role="tablist" aria-label="Assurance views">
          <button
            type="button"
            role="tab"
            id="assurance-tab-guarantees"
            aria-selected={tab === "guarantees"}
            aria-controls="assurance-panel-guarantees"
            className={`nema-assurance__tab${tab === "guarantees" ? " is-active" : ""}`}
            onClick={() => setTab("guarantees")}
          >
            {tab === "guarantees" ? <AssuranceIcon name="shield" /> : null}
            Our Core Guarantees
          </button>
          <button
            type="button"
            role="tab"
            id="assurance-tab-compare"
            aria-selected={tab === "compare"}
            aria-controls="assurance-panel-compare"
            className={`nema-assurance__tab${tab === "compare" ? " is-active" : ""}`}
            onClick={() => setTab("compare")}
          >
            NEMA vs Traditional Agencies
            <span className="nema-assurance__compare-pill">Compare</span>
          </button>
        </div>
      </div>

      {tab === "guarantees" ? (
        <div
          id="assurance-panel-guarantees"
          role="tabpanel"
          aria-labelledby="assurance-tab-guarantees"
          className="nema-assurance__grid"
        >
          {GUARANTEES.map((item) => (
            <article key={item.id} className="nema-assurance__card">
              <span className="nema-assurance__card-icon" aria-hidden>
                <AssuranceIcon name={item.icon} />
              </span>
              <h3 className="nema-assurance__card-title">{item.title}</h3>
              <p className="nema-assurance__card-body">{item.body}</p>
              <button
                type="button"
                className="nema-assurance__card-action"
                aria-label={`Explore ${item.title}`}
                onClick={onExplore}
              >
                <ArrowIcon />
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div
          id="assurance-panel-compare"
          role="tabpanel"
          aria-labelledby="assurance-tab-compare"
          className="nema-assurance__compare"
        >
          <div className="nema-assurance__compare-table" role="table" aria-label="NEMA vs traditional agencies">
            <div className="nema-assurance__compare-head" role="row">
              <div role="columnheader" className="nema-assurance__compare-feature-head">
                How it works
              </div>
              <div role="columnheader" className="nema-assurance__compare-col-head nema-assurance__compare-col-head--nema">
                NEMA CareHub (Direct-Hire)
              </div>
              <div role="columnheader" className="nema-assurance__compare-col-head nema-assurance__compare-col-head--agency">
                Traditional Agency
              </div>
            </div>
            {COMPARE_ROWS.map((row) => (
              <div key={row.feature} className="nema-assurance__compare-row" role="row">
                <div role="cell" className="nema-assurance__compare-feature">
                  {row.feature}
                </div>
                <div role="cell" className="nema-assurance__compare-nema">
                  {row.nema}
                </div>
                <div role="cell" className="nema-assurance__compare-agency">
                  {row.agency}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
